import React from 'react';
import { AlertCircle, Camera } from 'lucide-react';
import CustomerReturnScanner from '../../bottleReturns/components/CustomerReturnScanner';
import type { UpcEligibility } from '../../bottleReturns/types/bottleReturns.types';

interface ReturnUpcEntry {
  upc: string;
  quantity: number;
}

export interface CartReturnsSectionProps {
  isScannerOpen: boolean;
  onToggleScanner: () => void;
  hasCartItems: boolean;
  isAuthenticated: boolean;
  initialReturnUpcs: ReturnUpcEntry[];
  initialEligibilityCache: Record<string, UpcEligibility>;
  onStatePersist: (payload: {
    upcs: ReturnUpcEntry[];
    eligibilityCache: Record<string, UpcEligibility>;
    reason: 'hydrate' | 'add' | 'increment' | 'decrement' | 'remove' | 'clear';
  }) => void | Promise<void>;
  onChange: (totalContainers: number, estimatedCredit: number) => void;
  onUncountedBagChange?: (attached: boolean) => void;
  onComplete: () => void;
  returnSyncError: string | null;
}

const CartReturnsSection: React.FC<CartReturnsSectionProps> = ({
  isScannerOpen,
  onToggleScanner,
  hasCartItems,
  isAuthenticated,
  initialReturnUpcs,
  initialEligibilityCache,
  onStatePersist,
  onChange,
  onUncountedBagChange,
  onComplete,
  returnSyncError,
}) => {
  return (
    <>
      <div className="mb-6 bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <button
          type="button"
          onClick={onToggleScanner}
          className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-ninpo-lime/10 text-ninpo-lime rounded-xl">
              <Camera className="w-4 h-4" />
            </div>
            <div className="text-left">
              <p className="text-sm font-bold text-white">Scan Bottle Returns</p>
              <p className="text-xs text-slate-400">
                {hasCartItems
                  ? 'Credit applies on this order'
                  : 'Checkout schedules return-only pickup'}
              </p>
            </div>
          </div>
          <span className="text-xs font-bold text-ninpo-lime uppercase tracking-widest">
            {isScannerOpen ? 'Close' : 'Open'}
          </span>
        </button>
        {isScannerOpen && (
          <div className="p-4 border-t border-white/10 bg-black/40">
            <CustomerReturnScanner
              isAuthenticated={isAuthenticated}
              initialUpcs={initialReturnUpcs}
              initialEligibilityCache={initialEligibilityCache}
              onStatePersist={onStatePersist}
              onChange={onChange}
              onUncountedBagChange={onUncountedBagChange}
              onComplete={onComplete}
            />
          </div>
        )}
      </div>
      {returnSyncError ? (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-100">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p>{returnSyncError}</p>
        </div>
      ) : null}
    </>
  );
};

export default CartReturnsSection;
